import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Image, Alert, Spinner, Table, Modal, NavLink } from "react-bootstrap";
import { fetchWithAuth } from "../../Functions";
import plus from "../../assets/images/plus.svg";
import "../../assets/scss/pages/dashboard/dashboard.scss";

const ManageTeam = () => {
    const API = process.env.REACT_APP_API_BASE_URL;

    const [teamMembers, setTeamMembers] = useState([]);
    const [fetchFeedback, setFetchFeedback] = useState({ success: null, message: "" });
    const [deleteFeedBack, setDeleteFeedback] = useState({ success: null, message: "" });
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    // Feedback popup for delete member action
    const [showModal, setShowModal] = useState(false);
    const handleCloseModal = () => {
        setShowModal(false);
    }

    // Logic for fetching team members from API
    const fetchTeam = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await fetchWithAuth(`${API}/teams/`);

            // Handle 401
            if (response.status === 401) {
                console.error("Session expired. Redirecting to admin login page.");

                // Set a message to inform the user before the redirect
                setFetchFeedback({
                    success: false,
                    message: "Your session has expired. Please log in again."
                });

                // Navigate to admin login page
                navigate("/admin", { replace: true });

                // Stop further execution of the function
                return; 
            }

            // Handle other unsuccessful API responses
            if (!response.ok) {
                const errorData = await response.json();
                setFetchFeedback({ success: false, message: "Unable to load team members, please refresh page." });
                console.error(`Error fetching team members: ${errorData?.detail}` || `Error fetching team members:  ${errorData.status}`);
            }

            const data = await response.json();
            setTeamMembers(data.Teams);

            setFetchFeedback({ success: true, message: ""});
        } catch (error) {
            setFetchFeedback({ success: false, message: "An unexpeced error occured fetching team members." });
            console.error(`An error occured while trying to fetch team members: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    }, [API, navigate]);

    // Delete team member logic
    const deleteTeamMember = async (memberId, memberName) => {
        try {
            const response = await fetchWithAuth(`${API}/teams/delete/${memberId}/`, {
                method: 'DELETE',
            });

            // Handle 401
            if (response.status === 401) {
                console.error("Session expired. Redirecting to admin login page.");

                // Set a message to inform the user before the redirect
                setDeleteFeedback({
                    success: false,
                    message: "Your session has expired. Please log in again."
                });

                // Navigate to admin login page
                navigate("/admin", { replace: true });

                // Stop further execution of the function
                return; 
            }

            // Handle other unsuccessful API responses
            if (!response.ok) {
                const errorData = await response.json();
                const errorMessage = errorData?.detail || `Error removing team member: ${response.status} ${response?.statusText}`;
                setDeleteFeedback({ success: false, message: `Unable to delete ${memberName}, please try again.`});
                console.error(errorMessage);
            }

            // Update team state
            const updatedTeam = teamMembers.filter((member) => member.id !== memberId);
            setTeamMembers(updatedTeam);

            // Proceed to set successful feedback
            setDeleteFeedback({ success: true, message: `${memberName} removed successfully.`});
        } catch (error) {
            setDeleteFeedback({ success: false, message: `An unexpected error occured while deleting ${memberName}.`});
        }

        // Activate popup to display delete feedback
        setShowModal(true);
    }

    // Logic for updating team members
    const handleTeamMemberUpdate = (memberID) => {
        navigate("/dashboard/manage-team/edit", { state: {memberID} });
    }

    useEffect(() => {
        fetchTeam();
    }, [fetchTeam]);

    return (
        <>
            <h1 className="my-lg-3">Manage Team</h1>
            <Button className="px-4 py-2 my-3" href="/dashboard/manage-team/add">
                <Image src={ plus } /> Add team member
            </Button>

            {/* Fetch Team Alert */}
            { fetchFeedback.success === false && // Display this alert only when fetchListings fails
                <Alert variant="danger" className="text-center">{ fetchFeedback.message }</Alert>
            }

            {isLoading ? (
                <div className="text-center mt-5">
                    <Spinner animation="border" variant="primary" className="mx-auto" />
                    <p className="mt-2">Loading team members...</p>
                </div>
            ) : (
                teamMembers && (
                    <Table className="responsive-table my-3">
                        <thead>
                            <tr>
                                <th>Image</th>
                                <th>Name</th>
                                <th>Role</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            { teamMembers.map((member, index) => (
                                <tr key={ index }>
                                    <td className="preview-image-container"><Image src={ member.image } className="preview-image team-image" rounded /></td>
                                    <td data-label="Name">
                                        <NavLink href="/about/#team" className="text-decoration-underline text-primary">{ member.name }</NavLink>
                                    </td>
                                    <td data-label="Role">{ member.role }</td>
                                    <td data-label="Actions">
                                        <div>
                                            <Button className="me-2" variant="warning" onClick={() => handleTeamMemberUpdate(member.id)}>Edit</Button>
                                            <Button variant="danger" onClick={ () => deleteTeamMember(member.id, member.name)}>Delete</Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        { deleteFeedBack.success !== null &&
                            <Modal show={ showModal } onHide={ handleCloseModal } size="sm" centered>
                                <Modal.Header closeVariant="danger" closeButton>
                                    <Modal.Title as="h5">{ deleteFeedBack.success ? "Success" : "Failed!"}</Modal.Title>
                                </Modal.Header>
                                <Modal.Body as="p" className="fw-semibold">{ deleteFeedBack.message }</Modal.Body>
                            </Modal>
                        }
                    </Table>
                )
            )}
        </>
    )
}

export default ManageTeam;
